import { useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getLocalStorageJSON, setLocalStorageJSON } from './useLocalStorageJSON'

/**
 * Persist a value in localStorage backed by a TanStack Query cache entry.
 *
 * Reads from localStorage synchronously on mount (via `initialData`) and
 * writes to both localStorage and the query cache on updates, so every
 * component sharing the same key stays in sync without refetching.
 *
 * If the localStorage write fails (e.g. quota exceeded), the query cache
 * is not updated and the error is logged to the console.
 *
 * @param key - The localStorage key.
 * @param validate - Type-guard used to validate the stored JSON value.
 * @param defaultValue - Returned when localStorage has no valid entry.
 * @returns A `[value, setValue]` tuple.
 */
export function useLocalStorageQuery<T>(
  key: string,
  validate: (raw: unknown) => raw is T,
  defaultValue: T
): [T, (value: T | ((prev: T) => T)) => void] {
  const queryClient = useQueryClient()

  const getValue = () => getLocalStorageJSON(key, validate) ?? defaultValue

  const { data } = useQuery({
    queryKey: ['localStorage', key],
    queryFn: getValue,
    initialData: getValue,
    staleTime: Infinity,
    gcTime: Infinity,
    enabled: false,
  })

  const setValue = useCallback(
    (value: T | ((prev: T) => T)) => {
      const queryKey = ['localStorage', key]
      const current = queryClient.getQueryData<T>(queryKey) ?? defaultValue
      const next =
        typeof value === 'function' ? (value as (prev: T) => T)(current) : value

      try {
        setLocalStorageJSON(key, next)
      } catch (error) {
        console.error(`Failed to write localStorage key "${key}"`, error)
        return
      }

      queryClient.setQueryData(queryKey, next)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- defaultValue is stable per call site
    [key, queryClient]
  )

  return [data ?? defaultValue, setValue]
}
